import classNames from "classnames";
import { AnimatePresence } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import NumberEasing from "react-number-easing";

import resourceWoodImage from "../../assets/ui/icons/resource-wood.png";
import outcomeHeaderHelpImage from "../../assets/ui/help-hinder/hh-outcome-header-help.png";
import outcomeHeaderHinderImage from "../../assets/ui/help-hinder/hh-outcome-header-hinder.png";
import useChannelInfo from "../../hooks/useChannelInfo.js";
import useEbsApiClient from "../../hooks/useEbsApiClient.js";
import useTimers from "../../hooks/useTimers.js";
import useTwitch from "../../hooks/useTwitch.js";
import Button from "../Button/Button.js";
import EmbellishedHeading from "../EmbellishedHeading/EmbellishedHeading.js";
import Flyout from "../Flyout/Flyout.js";
import HelpHinderGauge from "../HelpHinderGauge/HelpHinderGauge.js";
import IconButton from "../IconButton/IconButton.js";
import Stack from "../Stack/Stack.js";

import "./HelpHinder.scss";
import SubtleText from "../SubtleText/SubtleText.js";
import Timer from "../Timer/Timer.js";

import AllEnemiesHaveModifiersInNextRoom from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-AllEnemiesHaveModifiersInNextRoom.png";
import AllFollwersPoopOrVomit from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-AllFollwersPoopOrVomit.png";
import BreakAllBeds from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-BreakAllBeds.png";
import ClearAllWaste from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-ClearAllWaste.png";
import CureCursedFollwers from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-CureCursedFollwers.png";
import DealDamageToAllEnemies from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-DealDamageToAllEnemies.png";
import DropPoisonOnAttack from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-DropPoisonOnAttack.png";
import GainFaith from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-GainFaith.png";
import GainRandomHeart from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-GainRandomHeart.png";
import GainTarot from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-GainTarot.png";
import HealHearts from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-HealHearts.png";
import IncreaseEnemyModifiersChance from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-IncreaseEnemyModifiersChance.png";
import InstantlyBuildStructures from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-InstantlyBuildStructures.png";
import InvincibleForTime from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-InvincibleForTime.png";
import KillRandomFollower from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-KillRandomFollower.png";
import LoseAllSpecialHearts from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-LoseAllSpecialHearts.png";
import RandomCursedState from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-RandomCursedState.png";
import RecieveDemon from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-RecieveDemon.png";
import ResetTempleCooldowns from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-ResetTempleCooldowns.png";
import ResurrectBuriedFollwer from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-ResurrectBuriedFollwer.png";
import SkipTime from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-SkipTime.png";
import SleepFollowers from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-SleepFollowers.png";
import SpawnBombs from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-SpawnBombs.png";
import TakeDamage from "../../assets/ui/help-hinder/outcome-icons/Twitch_Manipulations-TakeDamage.png";

const OUTCOME_IMAGES_BY_VALUE = {
  AllEnemiesHaveModifiersInNextRoom: AllEnemiesHaveModifiersInNextRoom,
  AllFollwersPoopOrVomit: AllFollwersPoopOrVomit,
  AllFollowersPoopOrVomit: AllFollwersPoopOrVomit,
  BreakAllBeds: BreakAllBeds,
  ClearAllWaste: ClearAllWaste,
  CureCursedFollwers: CureCursedFollwers,
  CureCursedFollowers: CureCursedFollwers,
  DealDamageToAllEnemies: DealDamageToAllEnemies,
  DropPoisonOnAttack: DropPoisonOnAttack,
  GainFaith: GainFaith,
  GainRandomHeart: GainRandomHeart,
  GainTarot: GainTarot,
  HealHearts: HealHearts,
  IncreaseEnemyModifiersChance: IncreaseEnemyModifiersChance,
  InstantlyBuildStructures: InstantlyBuildStructures,
  InvincibleForTime: InvincibleForTime,
  KillRandomFollower: KillRandomFollower,
  LoseAllSpecialHearts: LoseAllSpecialHearts,
  RandomCursedState: RandomCursedState,
  RecieveDemon: RecieveDemon,
  ReceiveDemon: RecieveDemon,
  ResetTempleCooldowns: ResetTempleCooldowns,
  ResurrectBuriedFollwer: ResurrectBuriedFollwer,
  ResurrectBuriedFollower: ResurrectBuriedFollwer,
  SkipTime: SkipTime,
  SleepFollowers: SleepFollowers,
  SpawnBombs: SpawnBombs,
  TakeDamage: TakeDamage,
};

const HH_EVENT_STATUSES = {
  HH_VOTING: "HH_VOTING",
  OUTCOME_VOTING: "OUTCOME_VOTING",
  COMPLETED: "COMPLETED",
};

const HelpHinder = React.memo(() => {
  const ebsApiClient = useEbsApiClient();
  const [hhEvent, setHhEvent] = useState(null);
  const [isHhVoteInProgress, setIsHhVoteInProgress] = useState(false);
  const { channel_name } = useChannelInfo();
  const timers = useTimers();
  const { t } = useTranslation();
  const { addPubSubEventListener, removePubSubEventListener } = useTwitch();

  const fetchHelpOrHinderStatus = async () => {
    const { data } = await ebsApiClient.get("/help-or-hinder");
    setHhEvent(data);
  };

  const voteForHelpHinderOption = async (option) => {
    try {
      setIsHhVoteInProgress(true);
      const { data } = await ebsApiClient.post("/help-or-hinder/vote", {
        hh_vote: option,
      });
      setHhEvent(data);
    } catch (err) {
    } finally {
      setIsHhVoteInProgress(false);
    }
  };

  const voteForOutcomeOption = async (optionValue) => {
    setHhEvent({
      ...hhEvent,
      outcome_user_vote: optionValue,
    });
    const { data } = await ebsApiClient.post("/help-or-hinder/vote", {
      outcome_vote: optionValue,
    });
    setHhEvent(data);
  };

  const handleHelpHinderBroadcast = (newEvent) => {
    setHhEvent((prevEvent) => {
      const shouldResetUserVotes =
        !prevEvent ||
        newEvent.status === "COMPLETED" ||
        (prevEvent.status === "COMPLETED" &&
          prevEvent.status !== newEvent.status);

      return {
        ...newEvent,
        hh_user_vote: shouldResetUserVotes ? null : prevEvent.hh_user_vote,
        outcome_user_vote: shouldResetUserVotes
          ? null
          : prevEvent.outcome_user_vote,
      };
    });
  };

  useEffect(() => {
    fetchHelpOrHinderStatus();
    addPubSubEventListener("cotl.hh.update", handleHelpHinderBroadcast);

    return () => {
      removePubSubEventListener("cotl.hh.update", handleHelpHinderBroadcast);
    };
  }, []);

  if (!hhEvent) {
    return null;
  }

  let gaugeProps = {};

  switch (hhEvent.status) {
    case HH_EVENT_STATUSES.HH_VOTING: {
      const totalVotes = hhEvent.hh_votes.help + hhEvent.hh_votes.hinder;
      gaugeProps.totalVotes = totalVotes;
      gaugeProps.helpPercentage =
        totalVotes === 0 ? 0.5 : hhEvent.hh_votes.help / totalVotes;
    }
  }

  const getOutcomeVotesAmountForOutcomeValue = (outcomeValue) => {
    return (
      hhEvent.outcome_votes.find((option) => option.value === outcomeValue)
        ?.votes || 0
    );
  };

  const numberEasingSpeed =
    timers?.helpHinder?.hhUpdateInterval * 1000 * 0.9 || 5500;

  return (
    <AnimatePresence>
      {hhEvent.status === HH_EVENT_STATUSES.HH_VOTING && (
        <Flyout key="hh-voting" direction="bottom">
          <Stack vertical spacing="default">
            <EmbellishedHeading>
              {t("features.helpOrHinder.hhVotingFlyout.title")}
            </EmbellishedHeading>
            <NumberEasing
              value={gaugeProps.helpPercentage}
              speed={numberEasingSpeed}
              decimals={2}
              ease="linear"
              customFunctionRender={(value) => (
                <HelpHinderGauge
                  key="help-hinder-gauge"
                  {...gaugeProps}
                  helpPercentage={value}
                  hinderPercentage={1 - value}
                  vote={hhEvent.hh_user_vote}
                  isHhVoteInProgress={isHhVoteInProgress}
                  onVote={voteForHelpHinderOption}
                />
              )}
            />

            <Stack vertical spacing="small">
              <SubtleText>
                {t("features.helpOrHinder.hhVotingFlyout.message")}
              </SubtleText>
              <SubtleText>
                <Timer
                  start={new Date(hhEvent.vote_started_at)}
                  duration={timers.helpHinder.hhVote}
                >
                  {({ difference, formatted }) =>
                    difference > 0
                      ? t(
                          "features.helpOrHinder.hhVotingFlyout.timeRemaining",
                          {
                            time: formatted,
                          }
                        )
                      : null
                  }
                </Timer>
              </SubtleText>
            </Stack>
          </Stack>
        </Flyout>
      )}
      {hhEvent.status === HH_EVENT_STATUSES.OUTCOME_VOTING && (
        <Flyout key="outcome-voting" direction="bottom">
          <Stack vertical spacing="default">
            <img
              className="hh-outcome__header-visual"
              src={
                hhEvent.hh_winning_option === "help"
                  ? outcomeHeaderHelpImage
                  : outcomeHeaderHinderImage
              }
              alt=""
            />
            <EmbellishedHeading>
              {t("features.helpOrHinder.outcomeVotingFlyout.title", {
                helpOrHinder: t(
                  `features.helpOrHinder.${hhEvent.hh_winning_option}`
                ),
                channelName: channel_name,
              })}
            </EmbellishedHeading>
            <Stack
              horizontal
              spacing="default"
              className={classNames("hh-outcome-options", {
                "hh-outcome-options--help":
                  hhEvent.hh_winning_option === "help",
                "hh-outcome-options--hinder":
                  hhEvent.hh_winning_option === "hinder",
              })}
            >
              {hhEvent[`${hhEvent.hh_winning_option}_outcome_options`].map(
                (outcome) => {
                  const iconImage = OUTCOME_IMAGES_BY_VALUE[outcome.value];

                  if (!iconImage) {
                    console.warn("No icon image found for", outcome.value);
                  }

                  return (
                    <div className="hh-outcome-option" key={outcome.value}>
                      <div className="hh-outcome-option__votes">
                        <NumberEasing
                          value={getOutcomeVotesAmountForOutcomeValue(
                            outcome.value
                          )}
                          speed={numberEasingSpeed}
                          decimals={0}
                          ease="linear"
                        />
                      </div>
                      <IconButton
                        isActive={outcome.value === hhEvent.outcome_user_vote}
                        icon={iconImage}
                        onClick={() => voteForOutcomeOption(outcome.value)}
                        disabled={Boolean(hhEvent.outcome_user_vote)}
                      />
                      <div className="hh-outcome-option__label">
                        {outcome.label}
                      </div>
                    </div>
                  );
                }
              )}
            </Stack>
            <SubtleText>
              <Timer
                start={new Date(hhEvent.vote_started_at)}
                duration={timers.helpHinder.outcomeVote}
              >
                {({ difference, formatted }) =>
                  difference > 0
                    ? t(
                        "features.helpOrHinder.outcomeVotingFlyout.timeRemaining",
                        {
                          time: formatted,
                        }
                      )
                    : null
                }
              </Timer>
            </SubtleText>
          </Stack>
        </Flyout>
      )}
    </AnimatePresence>
  );
});

export default HelpHinder;
